import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Award,
  GraduationCap,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  X,
} from "lucide-react";
import { Header } from "../sections/Header";
import { Footer } from "../sections/Footer";
import { StickyCTA } from "../sections/StickyCTA";
import { FIRM, BIO } from "../data/mock";
import {
  Seo,
  PERSON_SCHEMA,
  ORGANIZATION_SCHEMA,
  buildBreadcrumbSchema,
  buildWebPageSchema,
} from "../components/Seo";

const Bio = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Dick Bufkin — CPA Attorney & Forensic Accountant, Texas";
  const description =
    "Richard L. (Dick) Bufkin is a Texas attorney, Certified Public Accountant, and AICPA-certified forensic accountant with 50+ years of experience in business, family-law, probate, and bankruptcy litigation. Holds an LL.M. in Taxation from SMU, MBA, CPA license, and the AICPA Certified in Financial Forensics (CFF) credential.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path="/richard-bufkin"
        type="profile"
        keywords="Richard L. Bufkin attorney, Dick Bufkin CPA, forensic accountant Texas, AICPA CFF attorney, Mt. Pleasant Texas lawyer, business litigation attorney"
        schemas={[
          PERSON_SCHEMA,
          ORGANIZATION_SCHEMA,
          buildWebPageSchema({ title, description, path: "/richard-bufkin" }),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Richard L. Bufkin", path: "/richard-bufkin" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "@id": "https://bufkinlaw.com/richard-bufkin#profilepage",
            url: "https://bufkinlaw.com/richard-bufkin",
            mainEntity: { "@id": "https://bufkinlaw.com/richard-bufkin#person" },
            dateCreated: "2026-01-01",
            dateModified: new Date().toISOString().slice(0, 10),
          },
        ]}
      />
      <main data-testid="bio-page" className="bg-ivory min-h-screen">
      <Header />

      {/* Hero */}
      <section
        data-testid="bio-hero"
        className="relative pt-36 lg:pt-44 pb-20 lg:pb-28 bg-ivory overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <nav
            data-testid="bio-breadcrumb"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-navy/60 mb-10 fade-up"
          >
            <Link to="/" className="hover:text-navy transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" strokeWidth={2} />
            <span className="text-navy font-medium">Richard L. Bufkin</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6 fade-up-delay-1">
                <div className="gold-line" />
                <span
                  data-testid="bio-eyebrow"
                  className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium"
                >
                  Meet the Attorney
                </span>
              </div>
              <h1
                data-testid="bio-headline"
                className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-navy leading-[1.02] tracking-tight fade-up-delay-2"
              >
                Richard L.<br />
                <span className="italic text-gold">Bufkin.</span>
              </h1>
              <p
                data-testid="bio-title"
                className="mt-6 text-base lg:text-lg uppercase tracking-[0.22em] text-navy/70 font-medium fade-up-delay-3"
              >
                {BIO.title}
              </p>
              <p
                data-testid="bio-intro"
                className="mt-10 font-serif text-xl lg:text-2xl text-navy/85 leading-snug max-w-2xl fade-up-delay-3"
              >
                {BIO.intro}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4 fade-up-delay-4">
                <a
                  href="/#contact"
                  data-testid="bio-cta-primary"
                  className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
                >
                  Schedule Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href={`tel:${FIRM.phone.replace(/[^\d+]/g, "")}`}
                  data-testid="bio-cta-phone"
                  className="inline-flex items-center gap-3 px-2 py-4 text-sm text-navy font-medium hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4" strokeWidth={2} />
                  <span className="link-underline">{FIRM.phone}</span>
                </a>
              </div>
            </div>

            {/* Right: Portrait */}
            <div className="lg:col-span-5 fade-up-delay-2">
              <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto">
                <div className="absolute -top-4 -right-4 w-full h-full border-2 border-gold/40 rounded-sm" />
                <div className="relative w-full h-full overflow-hidden rounded-sm shadow-2xl bg-navy-deep">
                  <img
                    src="https://customer-assets.emergentagent.com/job_legal-finance-3/artifacts/wh5zrplf_No-Background-4.png"
                    alt="Richard L. Bufkin — Attorney, CPA, and AICPA-certified Forensic Accountant"
                    data-testid="bio-portrait"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/30 via-transparent to-navy-deep/20 pointer-events-none" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-navy text-ivory px-6 py-4 max-w-[280px] shadow-xl border-l-2 border-gold">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-1.5 font-medium">
                    Fifty Years
                  </div>
                  <div className="font-serif text-sm leading-snug text-ivory">
                    Of business and financial litigation experience.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy pull quote */}
      <section
        data-testid="bio-philosophy"
        className="py-20 lg:py-28 bg-navy text-ivory relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <span className="font-serif text-7xl lg:text-9xl text-gold/30 leading-none block">
            &ldquo;
          </span>
          <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory leading-[1.15] italic -mt-8">
            {BIO.philosophy}
          </blockquote>
          <div className="mt-10 inline-flex items-center gap-3">
            <div className="w-12 h-px bg-gold" />
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">
              Richard L. Bufkin
            </span>
            <div className="w-12 h-px bg-gold" />
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section
        data-testid="bio-credentials"
        className="py-20 lg:py-28 bg-cream"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
              <div className="flex items-center gap-3 mb-6">
                <div className="gold-line" />
                <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                  Credentials & Education
                </span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-navy leading-[1.05] tracking-tight">
                Trained twice<span className="text-gold">.</span>
                <br />
                In <span className="italic text-gold">law</span> and the
                <br />
                <span className="italic text-gold">numbers.</span>
              </h2>
              <p className="mt-6 text-base text-navy/75 leading-relaxed max-w-md">
                Most lawyers stop at the JD. Most CPAs stop at the CPA exam.
                Dick kept going through both — then earned the AICPA's
                forensic accounting credential on top.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 p-5 border border-gold/30 bg-white rounded-sm">
                <Award className="w-7 h-7 text-gold" strokeWidth={1.25} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-navy/60">
                    Rare Combination
                  </div>
                  <div className="font-serif text-sm text-navy leading-tight">
                    Attorney + CPA + AICPA CFF
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <ul className="space-y-px">
                {BIO.credentials.map((cred, idx) => (
                  <li
                    key={idx}
                    data-testid={`credential-${idx}`}
                    className="bg-white border-l-2 border-gold p-6 lg:p-8 hover:translate-x-1 transition-transform"
                  >
                    <div className="flex items-start gap-5">
                      <div className="font-serif text-4xl text-gold/40 w-12 flex-shrink-0">
                        0{idx + 1}
                      </div>
                      <div>
                        <h3 className="font-serif text-2xl text-navy leading-tight">
                          {cred.label}
                        </h3>
                        <p className="mt-2 text-sm lg:text-base text-navy/75 leading-relaxed">
                          {cred.detail}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Career timeline */}
      <section
        data-testid="bio-experience"
        className="py-20 lg:py-28 bg-ivory"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                Career & Practice
              </span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-navy leading-[1.05] tracking-tight">
              A practice built
              <br />
              <span className="italic text-gold">case by case</span>, for
              fifty years.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BIO.experience.map((exp, idx) => (
              <div
                key={idx}
                data-testid={`experience-${idx}`}
                className="bg-white border border-[#C0974E]/20 p-7 lg:p-8 hover:border-gold/50 transition-all hover:shadow-lg hover:shadow-navy/5"
              >
                <Briefcase className="w-5 h-5 text-gold mb-5" strokeWidth={1.5} />
                <div className="font-serif text-2xl text-navy leading-tight">
                  {exp.year}
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-navy/60 mt-1 mb-4">
                  {exp.title}
                </div>
                <p className="text-sm text-navy/75 leading-relaxed">
                  {exp.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Representative matters */}
      <section
        data-testid="bio-matters"
        className="py-20 lg:py-28 bg-cream"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <div className="flex items-center gap-3 mb-5">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                Representative Matters
              </span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-navy leading-[1.05] tracking-tight">
              The kinds of cases
              <br />
              <span className="italic text-gold">that come</span> to Dick.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-navy/75 leading-relaxed max-w-md">
              Specifics vary — and confidentiality is sacred — but most engagements fall into one or more of these categories.
            </p>

            <div className="mt-10 p-6 bg-white border border-[#C0974E]/20 rounded-sm">
              <div className="flex items-center gap-3 mb-3">
                <X className="w-4 h-4 text-navy/40" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.22em] text-navy/60 font-medium">
                  Not Handled
                </span>
              </div>
              <ul className="space-y-2">
                {BIO.doesNotHandle.map((item) => (
                  <li key={item} className="text-sm text-navy/70 font-serif">
                    · {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="space-y-px">
              {BIO.representativeMatters.map((matter, idx) => (
                <li
                  key={idx}
                  data-testid={`matter-${idx}`}
                  className="flex items-start gap-5 py-5 lg:py-6 border-b border-[#C0974E]/20 group hover:border-gold/50 transition-colors"
                >
                  <CheckCircle2
                    className="w-4 h-4 text-gold flex-shrink-0 mt-1.5"
                    strokeWidth={1.5}
                  />
                  <span className="text-base lg:text-lg font-serif text-navy leading-snug pt-0.5">
                    {matter}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Recognition / memberships */}
      <section
        data-testid="bio-recognition"
        className="py-20 lg:py-24 bg-ivory"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-3 mb-6 justify-center">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
              Memberships & Recognition
            </span>
            <div className="gold-line" />
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl text-navy leading-tight tracking-tight max-w-3xl mx-auto">
            Affiliations that reflect the dual practice.
          </h2>
          <ul className="mt-12 grid sm:grid-cols-2 gap-px bg-[#C0974E]/15 border border-[#C0974E]/20 rounded-sm overflow-hidden">
            {BIO.recognition.map((item, idx) => (
              <li
                key={idx}
                data-testid={`recognition-${idx}`}
                className="bg-ivory p-6 lg:p-8 flex items-center gap-4"
              >
                <GraduationCap className="w-5 h-5 text-gold flex-shrink-0" strokeWidth={1.5} />
                <span className="text-base font-serif text-navy">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="bio-cta-section"
        className="py-20 lg:py-28 bg-cream"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight">
            Have a matter that needs
            <br />
            <span className="italic text-gold">both</span> hats?
          </h2>
          <p className="mt-8 text-base lg:text-lg text-navy/75 max-w-2xl mx-auto leading-relaxed">
            That's exactly when Dick is most useful. An initial
            consultation is the right place to start.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="/#contact"
              data-testid="bio-final-cta"
              className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/practice-areas"
              data-testid="bio-practice-link"
              className="btn-outline inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
            >
              View Practice Areas
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </main>
    </>
  );
};

export default Bio;
